import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import pluralize from 'pluralize';
import { Link, useLocation } from 'react-router-dom';
import ToolTipIcon from '../../../components/ToolTipIcon/ToolTipIcon';
import { addNotification } from '../../../actions/notificationActions';
import { getPermissions } from '../../../utils/getPermissions';
import DropDown from '../../../components/DropDown/DropDown';
import urlHelper from '../../../utils/urlHelper';

function AssignmentActions(props) {
  const {
    auth,
    assignSign,
    assignSequence,
    assignSchedule,
    assignPlaylist,
    assignChannel,
    deployAssignment,
    removeAssignment,
    selectedAssignments = {},
    changeCampaign,
    overrideDownloadSchedule,
  } = props;
  const permissions = getPermissions(auth, 'devices');
  const selectedAssignmentsLength = Object.keys(selectedAssignments).length;
  const [showUploadDropDown, setShowUploadDropDown] = useState(false);
  const showUploadDropDownTogglerRef = useRef(null);
  const userLocation = auth.user.user.userLocation;
  const location = useLocation();
  const homeUrl = userLocation
    ? urlHelper.setUrlParam(location.search, 'parent', userLocation)
    : null;

  return (
    <div
      data-test="assignment-actions"
      className="grid grid-cols-[repeat(5,_max-content)] md:grid-cols-[repeat(7,_max-content)] xl:grid-cols-[repeat(14,_max-content)] gap-2 mt-8"
    >
      <ToolTipIcon toolText="Change Campaign">
        <button
          data-test="location-copy-setting"
          type="button"
          onClick={changeCampaign}
          className="duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
        >
          <img
            src="/assets/images/icons/speakerphone.svg"
            alt="Copy"
            className="w-[30px] h-[30px]"
            draggable={false}
          />
        </button>
      </ToolTipIcon>

      {selectedAssignmentsLength > 0 && (
        <ToolTipIcon toolText="Deploy Content">
          <button
            type="button"
            onClick={deployAssignment}
            className="duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
          >
            <img
              src="/assets/images/icons/deploy_button.svg"
              alt="Deploy"
              className="w-[30px] h-[30px]"
              draggable={false}
            />
          </button>
        </ToolTipIcon>
      )}
      {selectedAssignmentsLength > 0 &&
        permissions.atLeastTierProOrSuperUser &&
        permissions.downloadSchedule && (
          <ToolTipIcon toolText="Override Download Schedule">
            <button
              data-test="device-actions-deploy-content"
              type="button"
              onClick={overrideDownloadSchedule}
              className="duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
            >
              <img
                src="/assets/images/icons/download_schedule.svg"
                alt="Override Download Schedule"
                className="w-[30px] h-[30px]"
                draggable={false}
              />
            </button>
          </ToolTipIcon>
        )}

      {selectedAssignmentsLength > 0 && permissions.atLeastTierProOrSuperUser && (
        <ToolTipIcon toolText={pluralize('Remove Assignment', selectedAssignmentsLength)}>
          <button
            type="button"
            onClick={() => {
              if (Object.keys(selectedAssignments).length === 0) {
                const text = <span>Please select an assignment</span>;

                addNotification({ type: 'danger', text });
              } else {
                removeAssignment();
              }
            }}
            className="duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
          >
            <img
              src="/assets/images/icons/delete_button.svg"
              alt="Delete"
              className="w-[30px] h-[30px]"
              draggable={false}
            />
          </button>
        </ToolTipIcon>
      )}
      {permissions.atLeastTierProOrSuperUser && selectedAssignmentsLength > 0 && (
        <div className="relative h-[50px]">
          <ToolTipIcon toolText="Assign Content">
            <button
              data-test="assignment-actions-dropdown"
              ref={showUploadDropDownTogglerRef}
              type="button"
              className="w-[50px] relative cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
              onClick={() => {
                setShowUploadDropDown(!showUploadDropDown);
              }}
            >
              <img
                src="/assets/images/icons/upload_icon.svg"
                alt="Assignment actions"
                className="w-[50px] h-[50px] cursor-pointer"
                draggable={false}
              />
            </button>
          </ToolTipIcon>

          <DropDown
            show={showUploadDropDown}
            hide={() => setShowUploadDropDown(false)}
            togglerRef={showUploadDropDownTogglerRef}
            dataTest="dropdown-AssignContent"
          >
            <div className="relative max-h-[500px] overflow-y-auto md:translate-x-0 -translate-x-1/2">
              <div className="border-t border-gray-200 divide-y divide-gray-200 border-b-200">
                <button
                  data-test="AssignContent-AssignaSign"
                  type="button"
                  className="appearance-none w-[200px] h-[40px] bg-slate-50 hover:bg-gray-100"
                  onClick={assignSign}
                >
                  Assign a Sign
                </button>

                <button
                  data-test="AssignContent-AssignaSequence"
                  type="button"
                  className="appearance-none w-[200px] h-[40px] bg-slate-50 hover:bg-gray-100"
                  onClick={assignSequence}
                >
                  Assign a Sequence
                </button>
                <button
                  data-test="AssignContent-AssignaSchedule"
                  type="button"
                  className="appearance-none w-[200px] h-[40px] bg-slate-50 hover:bg-gray-100"
                  onClick={assignSchedule}
                >
                  Assign a Schedule
                </button>
                <button
                  data-test="AssignContent-AssignaPlaylist"
                  type="button"
                  className="appearance-none w-[200px] h-[40px] bg-slate-50 hover:bg-gray-100"
                  onClick={assignPlaylist}
                >
                  Assign a Playlist
                </button>
                <button
                  data-test="AssignContent-AssignaChannel"
                  type="button"
                  className="appearance-none w-[200px] h-[40px] bg-slate-50 hover:bg-gray-100"
                  onClick={assignChannel}
                >
                  Assign a Channel
                </button>
              </div>
            </div>
          </DropDown>
        </div>
      )}

      {homeUrl && (
        <ToolTipIcon toolText="Assignment Home">
          <Link
            to={homeUrl}
            className="duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
          >
            <img
              src="/assets/images/icons/black_house_icon.svg"
              alt="Home"
              className="w-[30px] h-[30px]"
              draggable={false}
            />
          </Link>
        </ToolTipIcon>
      )}
    </div>
  );
}

AssignmentActions.propTypes = {
  auth: PropTypes.object.isRequired,
  assignSign: PropTypes.func,
  assignSequence: PropTypes.func,
  assignSchedule: PropTypes.func,
  assignPlaylist: PropTypes.func,
  assignChannel: PropTypes.func,
  deployAssignment: PropTypes.func,
  removeAssignment: PropTypes.func,
  selectedAssignments: PropTypes.object,
  changeCampaign: PropTypes.func,
  overrideDownloadSchedule: PropTypes.func,
};
const mapDispatchToProps = {};
const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps, mapDispatchToProps)(AssignmentActions);
